import { CopyableCell } from '../../components/ui/CopyableCell';
import {
  renderRowHeadlineArray,
  renderRowTemplate,
  renderRowTemplateArray,
  slugifyPath,
  type RowRenderContext,
} from '../../lib/rowContent';
import type { AccountCampaign } from '../../types/accountCampaign';
import type { GoogleAssets } from '../../types/campaign';
import { BuildViewSection } from './BuildViewSection';
import {
  DriftDot,
  type BuildViewDriftFlags,
} from './BuildViewModificationBadges';
import { SectionBadge } from './BuildViewMetaContent';
import { VariationsListInline } from './VariationsListInline';

// P2b-3 Build View commit 4: Google-specific content blocks.
//
// Order per spec §2.7:
//   - Headlines (variations cards)
//   - Descriptions (variations cards)
//   - Path1 (single field with copy)
//   - Path2 (single field with copy)
//
// Mirrors BuildViewMetaContent but with the Google asset shape — no
// body copy, CTA, or lead form on Google. Each authored string passes
// through renderRowTemplate so per-location placeholders substitute.

type Props = {
  row: AccountCampaign;
  renderCtx: RowRenderContext;
  drift: BuildViewDriftFlags;
};

export function BuildViewGoogleContent({ row, renderCtx, drift }: Props) {
  const assets: GoogleAssets | null = row.googleAssets;
  // Pinned Headlines spec §2.3: render text + pin together so the
  // variation cards show a P1/P2/P3 badge alongside the text. The
  // Copy + Copy-all buttons still copy text-only — pin metadata
  // never enters the clipboard string.
  const headlineEntries = renderRowHeadlineArray(assets?.headlines ?? null, renderCtx);
  const headlines = headlineEntries.map((h) => h.text);
  const headlinePins = headlineEntries.map((h) => h.pinPosition);
  const descriptions = renderRowTemplateArray(
    assets?.descriptions ?? null,
    renderCtx,
  );
  const path1 = renderRowTemplate(assets?.path1 ?? null, renderCtx);
  // Google Ads paths reject spaces — slugify path2 after template
  // substitution. Path1 stays as-authored.
  const path2 = slugifyPath(renderRowTemplate(assets?.path2 ?? null, renderCtx));

  return (
    <>
      <BuildViewSection
        id="bv-headlines"
        title="Headlines"
        badge={
          <SectionBadge
            modified={drift.headlines}
            modifiedTitle="Headlines modified from Campaign"
            variations={headlines}
          />
        }
      >
        <VariationsListInline variations={headlines} pins={headlinePins} />
      </BuildViewSection>

      <BuildViewSection
        id="bv-descriptions"
        title="Descriptions"
        badge={
          <SectionBadge
            modified={drift.descriptions}
            modifiedTitle="Descriptions modified from Campaign"
            variations={descriptions}
          />
        }
      >
        <VariationsListInline variations={descriptions} />
      </BuildViewSection>

      <BuildViewSection
        id="bv-path1"
        title="Path 1"
        badge={<DriftDot visible={drift.path1} title="Path 1 modified from Campaign" />}
      >
        {path1 ? (
          <CopyableCell
            value={path1}
            ariaLabel="Path 1"
            display={<span className="text-sm font-mono">{path1}</span>}
          />
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </BuildViewSection>

      <BuildViewSection
        id="bv-path2"
        title="Path 2"
        badge={<DriftDot visible={drift.path2} title="Path 2 modified from Campaign" />}
      >
        {path2 ? (
          <CopyableCell
            value={path2}
            ariaLabel="Path 2"
            display={<span className="text-sm font-mono">{path2}</span>}
          />
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </BuildViewSection>
    </>
  );
}
